'use client';

import { TrafficOriginType } from '@cc-shopping/contracts/credit-cards-page/constants';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { Details } from '@cc-shopping/ui/primitives/Details';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { startTransition, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { MoreIcon } from './Icons/MoreIcon';
import { SeeAllDesktopMenu } from './SeeAllDesktopMenu';
import { SeeAllMobileMenu } from './SeeAllMobileMenu';
type SeeAllButtonProps = {
  analyticsProps: {
    featureViewedActivityId: string;
  };
  activeNavId: string;
  trafficOriginType?: TrafficOriginType;
};
export const SeeAllButton = ({
  analyticsProps,
  activeNavId,
  trafficOriginType = TrafficOriginType.Organic
}: SeeAllButtonProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (isMenuOpen && !isMobile && !detailsRef.current?.parentNode?.contains(event.target as Node)) {
        handleCloseMenu();
      }
    };
    const handleKeyDown = (event_0: KeyboardEvent) => {
      if (isMenuOpen && event_0.key === 'Escape') {
        handleCloseMenu();
      }
    };
    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);
  const firePageAction = usePageAction({
    actionName: 'see_all_dropdown',
    actionSection: 'cc_sub_nav',
    activityId: uuid()
  });
  const handleNavButtonClick = (newMenuState: boolean) => {
    startTransition(() => {
      setIsMenuOpen(newMenuState);
    });
    firePageAction({
      action_type: newMenuState ? 'show' : 'hide'
    });
  };
  const handleCloseMenu = () => {
    startTransition(() => {
      setIsMenuOpen(false);
    });
  };
  return <Details variant="simple" SummaryComponent={<>
          <MoreIcon selected={isMenuOpen} />
          <Typography component="span" sx={{
      alignSelf: 'center',
      color: 'inherit',
      display: 'flex',
      fontSize: {
        xs: '11px',
        sm: '14px'
      },
      fontWeight: '500'
    }}>
            See all
            <Box component="span" className="mask-icon mask-icon-expand-more" sx={{
        width: {
          xs: '16px',
          sm: '20px'
        },
        height: {
          xs: '16px',
          sm: '20px'
        }
      }} />
          </Typography>
        </>} detailsProps={{
    open: isMenuOpen,
    ref: detailsRef,
    onToggle: ({
      newState
    }) => {
      handleNavButtonClick(newState === 'open');
    },
    sx: {
      borderBottom: '2px solid',
      borderColor: 'transparent',
      color: 'neutral.darker',
      height: '100%',
      paddingY: 2,
      '&:hover': {
        borderColor: 'grey.300'
      },
      '&[open]': {
        borderColor: 'greens.main'
      },
      '&[open] summary': {
        color: 'greens.main'
      }
    }
  }} summaryComponentProps={{
    sx: {
      alignItems: 'center',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      justifyContent: 'center',
      position: 'relative',
      zIndex: 2
    }
  }}>
      {isMobile ? <SeeAllMobileMenu activeNavId={activeNavId} featureViewedActivityId={analyticsProps.featureViewedActivityId} handleCloseMenu={handleCloseMenu} isMenuOpen={isMenuOpen} trafficOriginType={trafficOriginType} /> : <SeeAllDesktopMenu activeNavId={activeNavId} detailsRef={detailsRef} featureViewedActivityId={analyticsProps.featureViewedActivityId} handleCloseMenu={handleCloseMenu} isMenuOpen={isMenuOpen} trafficOriginType={trafficOriginType} />}
    </Details>;
};